import React, {
  useState,
  FocusEvent,
  forwardRef,
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useController, UseControllerProps } from "react-hook-form";
import classNames from "classnames";
import styles from "./styles.module.scss";
import useDebounce from "src/hooks/useDebounce";
import { DropDown, StreetAndCityResponse } from "src/utils/types";
import { images } from "src/assets/images/images";
import {
  API_RESULT,
  AUTOCOMPLETE,
  INPUT_TYPE,
  INPUT_SPECIAL_TYPE,
} from "src/utils/enums";
import { useAppDispatch, useAppSelector } from "src/hooks/useRedux";
import {
  setCities,
  setSelectedCity,
  setSelectedStreet,
  setStreets,
} from "src/redux/slices/userSlice/UserSlice";
import ScreenReaderText from "src/components/accessibilityKit/screenReaderText";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import 'dayjs/locale/he'; // Import Hebrew locale
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ErrorMessage from "src/components/errorMessage";

// Configure dayjs plugins and locale
dayjs.extend(customParseFormat);
dayjs.locale('he');

// Constants for date formats
const DATE_FORMAT_STORAGE = "YYYY-MM-DD";
const DATE_FORMAT_DISPLAY = "DD/MM/YYYY";
const AUTOCOMPLETE_BLUR_DELAY = 150;

interface FloatingLabelInputProps extends UseControllerProps {
  id?: string;
  label: string;
  ariaLabel?: string;
  type?: INPUT_TYPE;
  style?: string;
  disabled?: boolean;
  autoCompleteType?: string;
  results?: StreetAndCityResponse[]; // Optional prop for results
  countriesResults?: { value: string; label: string }[]; // Optional prop for results
  loading?: boolean | null; // Optional prop for loading state
  fetchSuggestions?: (value: string) => void; // Optional prop for fetching suggestions
  resStatus?: string;
  crearByInput?: () => void; // Add the clearZip prop
  defaultInputValue?: string;
  readOnly?: boolean;
  inputType?: INPUT_SPECIAL_TYPE; // Add this new prop
  options?: Array<{ value: string; label: string }>; // Add this for dropdown options
  errorDescribedBy?: string;
  debounceLength?: number;
  isMandatory?: boolean;
  maxLength?: number;
}

const FloatingLabelInput = forwardRef<
  HTMLInputElement | HTMLSelectElement,
  FloatingLabelInputProps
>(
  (
    {
      id,
      label,
      ariaLabel,
      type = INPUT_TYPE.TEXT,
      style,
      name,
      disabled,
      autoCompleteType = AUTOCOMPLETE.NONE,
      results = [], // Default to an empty array if not provided
      loading, // Default to false if not provided
      fetchSuggestions,
      resStatus = API_RESULT.LOADING,
      crearByInput,
      defaultInputValue,
      readOnly = false,
      inputType = INPUT_SPECIAL_TYPE.TEXT,
      options = [],
      errorDescribedBy,
      countriesResults = [],
      debounceLength = 2,
      isMandatory = false,
      maxLength,
    },
    ref
  ) => {
    const dispatch = useAppDispatch();

    const { strings } = useAppSelector((state) => state.app);
    const [isFocused, setIsFocused] = useState(false);
    const [inputValue, setInputValue] = useState<string>("");
    const [srId, setSrId] = useState<string>("");
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const [error, setError] = useState<string>("");
    const { selectedCity, selectedStreet } = useAppSelector((state) => state.user);
    const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const debouncedInputValue = useDebounce(inputValue, 500, debounceLength);

    const {
      field: { value, onChange, onBlur },
    } = useController({
      name,
      defaultValue: defaultInputValue || "",
    });

    const cancelPendingBlur = () => {
      if (blurTimeoutRef.current !== null) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    };

    useEffect(() => () => {
      if (blurTimeoutRef.current !== null) {
        clearTimeout(blurTimeoutRef.current);
      }
    }, []);

    // Parse date with strict validation
    const parsedDate = useMemo(() => {
      if (!value) return undefined;
      const parsed = dayjs(value, DATE_FORMAT_STORAGE, true);
      return parsed.isValid() ? parsed : undefined;
    }, [value]);

    useEffect(() => {
      if (debouncedInputValue && fetchSuggestions) {
        fetchSuggestions(debouncedInputValue);
      }
      setSrId(`${name}-${label}-${debouncedInputValue}`);
    }, [debouncedInputValue]);
    
    useEffect(() => {
      if (disabled || inputValue.length == 0) {
        clear();
      }
    }, [disabled, inputValue]);

    useEffect(() => {
      // Sync inputValue with value or defaultInputValue when they change
      const formValue = defaultInputValue || value || "";
      if (formValue !== inputValue) {
        setInputValue(formValue);
      }
    }, [value, defaultInputValue]);

    useEffect(() => {
      const handleClickOutside = (event: globalThis.MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
      // Detect when value is cleared externally (by parent component)
      if (value === "" && inputValue !== "") {
        setInputValue("");
      }
    }, [value]);

    const handleResultClick = (selectedValue: StreetAndCityResponse) => {
      cancelPendingBlur();
      setInputValue(selectedValue.name_hebrew);
      onChange(selectedValue.name_hebrew);
      setError("");
      
      if (autoCompleteType == AUTOCOMPLETE.CITY) {
        dispatch(setSelectedCity({ name: selectedValue.name_hebrew, id: selectedValue.city_id }));
      } else if (autoCompleteType == AUTOCOMPLETE.ADDRESS) {
        dispatch(setSelectedStreet({ name: selectedValue.name_hebrew, id: selectedValue.street_id }));
      } else if (autoCompleteType == AUTOCOMPLETE.NATIONALITY) {
        onChange(selectedValue);
      }

      setDropdownVisible(false);
      setIsFocused(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Handle numeric input restriction
      if (
        ["numeric", "number", "tel"].includes(type) &&
        !/[0-9]/.test(event.key) &&
        event.key !== "Backspace" &&
        event.key !== "Delete"
      ) {
        event.preventDefault();
        return;
      }

      // Handle dropdown navigation
      if (!dropdownVisible || results.length === 0) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev < results.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          event.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case "Enter":
        // case " ": // Space
          event.preventDefault();
          if (selectedIndex >= 0) {
            handleResultClick(results[selectedIndex]);
          }
          break;
      }
    };

    const renderedResults = useMemo(() => {
      if (dropdownVisible && resStatus == API_RESULT.SUCCEEDED) {
        return (
          <div className={styles.results}>
            {results.length > 0 ? (
              results.map((result: StreetAndCityResponse, index: number) => (
                <div
                  key={index}
                  className={classNames(styles.resultItem, {
                    [styles.selected]: index === selectedIndex,
                  })}
                  onMouseDown={(event) => {
                    // Keep the input focused until click selects the result, even on a held press.
                    if (event.button === 0) event.preventDefault();
                  }}
                  onClick={() => handleResultClick(result)}
                >
                  {result.name_hebrew}
                </div>
              ))
            ) : (
              <div className={styles.noResultItem}>
                <ScreenReaderText
                  id={srId}
                  text={strings.acc_t_no_results_found}
                  announnceDelay={700 + results?.length * 100}
                />
                {strings.t_no_results_found}
              </div>
            )}
          </div>
        );
      }
      return null;
    }, [dropdownVisible, results, loading, selectedIndex]);

    const renderedCountriesResults = useMemo(() => {
      if (dropdownVisible && countriesResults.length > 0) {
        const filteredResults = countriesResults.filter(
          (result) =>
            result.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            result.value.toLowerCase().includes(inputValue.toLowerCase())
        );

        return (
          <div className={styles.results}>
            {filteredResults.map(
              (result: { value: string; label: string }, index: number) => (
                <div
                  key={index}
                  className={classNames(styles.resultItem, {
                    [styles.selected]: index === selectedIndex,
                  })}
                  onClick={() => {
                    setInputValue(result.label);
                    onChange(result.value);
                    setDropdownVisible(false);
                  }}
                >
                  {result.label}
                </div>
              )
            )}
          </div>
        );
      }
      return null;
    }, [dropdownVisible, countriesResults, inputValue, selectedIndex]);

    const handleFocus = (
      event: FocusEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
      cancelPendingBlur();
      setIsFocused(true);
      if (autoCompleteType === AUTOCOMPLETE.NATIONALITY) {
        setDropdownVisible(true);
      } else if (inputValue.length > 1) {
        setDropdownVisible(true);
      }
    };

    const handleBlur = (
      event: FocusEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
      onBlur();

      cancelPendingBlur();
      blurTimeoutRef.current = setTimeout(() => {
        if (autoCompleteType === AUTOCOMPLETE.CITY || autoCompleteType === AUTOCOMPLETE.ADDRESS) {
          const selection = autoCompleteType === AUTOCOMPLETE.CITY
            ? selectedCity : selectedStreet;
          const hasSelection = selection.id && selection.name === value;
          const exactResult = results.find(
            (result) => result.name_hebrew.trim() === value.trim()
          );
          if (autoCompleteType === AUTOCOMPLETE.CITY && !hasSelection && exactResult && resStatus === API_RESULT.SUCCEEDED) {
            handleResultClick(exactResult);
          }
          setError(value && resStatus === API_RESULT.SUCCEEDED && !hasSelection && !exactResult
            ? strings.t_please_select_from_the_list : "");
        }
        setDropdownVisible(false);
        setIsFocused(false);
        blurTimeoutRef.current = null;
      }, AUTOCOMPLETE_BLUR_DELAY);
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      cancelPendingBlur();
      const newValue = event.target.value;
      setInputValue(newValue);
      onChange(newValue);
      setSelectedIndex(-1);
      if (autoCompleteType === AUTOCOMPLETE.CITY) {
        dispatch(setSelectedCity({ name: "", id: "" }));
      } else if (autoCompleteType === AUTOCOMPLETE.ADDRESS) {
        dispatch(setSelectedStreet({ name: "", id: "" }));
      }
      setError("");
      
      if (autoCompleteType === AUTOCOMPLETE.NATIONALITY) {
        setDropdownVisible(true);
      } else {
        setDropdownVisible(!!newValue && newValue.length > 1);
      }
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        ["numeric", "number", "tel"].includes(type) &&
        !/[0-9]/.test(event.key) &&
        event.key !== "Backspace" &&
        event.key !== "Delete"
      ) {
        event.preventDefault();
      }
    };

    const clear = () => {
      cancelPendingBlur();
      setInputValue("");
      onChange("");
      onBlur();
      setSelectedIndex(-1);
      setError("");

      setDropdownVisible(false);
      if (autoCompleteType == AUTOCOMPLETE.CITY) {
        dispatch(setSelectedCity({ name: "", id: "" }));
        dispatch(setCities([]));
      } else if (autoCompleteType == AUTOCOMPLETE.ADDRESS) {
        dispatch(setSelectedStreet({ name: "", id: "" }));
        dispatch(setStreets([]));
      } else if (autoCompleteType == AUTOCOMPLETE.NATIONALITY) {
        onChange("");
      }
      if (crearByInput) {
        crearByInput();
      }
    };

    const handleDatePickerFocus = () => {
      setIsFocused(true);
    };

    const handleDatePickerBlur = () => {
      onBlur();
      setTimeout(() => {
        setIsFocused(false);
      }, 200);
    };

    return (
      <div
        className={classNames(
          `${styles.floatingLabelInput} ${
            isFocused || value || defaultInputValue || inputType === INPUT_SPECIAL_TYPE.DATE
              ? styles.focused
              : ""
          }`,
          { [styles.disabled]: disabled },
          style
        )}
      >
        {inputType === INPUT_SPECIAL_TYPE.DROPDOWN ? (
          <div className={styles.selectWrapper} ref={dropdownRef}>
            <div
              className={classNames(styles.customSelect, {
                [styles.disabled]: readOnly || disabled,
                [styles.open]: isOpen,
              })}
              onClick={() => !disabled && !readOnly && setIsOpen(!isOpen)}
              onKeyDown={(e) => {
                if (disabled || readOnly) return;

                switch (e.key) {
                  case "Enter":
                  case " ":
                    e.preventDefault();
                    if (!isOpen) {
                      setIsOpen(true);
                      setHighlightedIndex(0);
                    } else if (highlightedIndex >= 0) {
                      const selectedOption = options[highlightedIndex];
                      setInputValue(selectedOption.value);
                      onChange(selectedOption.value);
                      setIsOpen(false);
                      setHighlightedIndex(-1);
                    }
                    break;
                  case "ArrowDown":
                    e.preventDefault();
                    if (!isOpen) {
                      setIsOpen(true);
                      setHighlightedIndex(0);
                    } else {
                      setHighlightedIndex((prev) =>
                        prev < options.length - 1 ? prev + 1 : 0
                      );
                    }
                    break;
                  case "ArrowUp":
                    e.preventDefault();
                    if (!isOpen) {
                      setIsOpen(true);
                      setHighlightedIndex(options.length - 1);
                    } else {
                      setHighlightedIndex((prev) =>
                        prev > 0 ? prev - 1 : options.length - 1
                      );
                    }
                    break;
                  case "Escape":
                    e.preventDefault();
                    setIsOpen(false);
                    setHighlightedIndex(-1);
                    break;
                  case "Tab":
                    setIsOpen(false);
                    setHighlightedIndex(-1);
                    break;
                }
              }}
              tabIndex={disabled || readOnly ? -1 : 0}
              role="combobox"
              {...(errorDescribedBy
                ? { "aria-describedby": errorDescribedBy }
                : {})}
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              aria-activedescendant={`option-${options[highlightedIndex]?.label}`}
              aria-controls={`options-listbox-${name}`}
              aria-labelledby={`${name}-${label}`}
            >
              <span id={"selected-option"} className={styles.selectedValue}>
                {options.find((opt) => opt.value === value)?.label}
              </span>
              <div className={styles.selectArrow}>
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                  <path
                    d="M1 1L5 5L9 1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            {isOpen && (
              <div
                className={styles.optionsList}
                role="listbox"
                id={`options-listbox-${name}`}
              >
                {options.map((option, index) => (
                  <div
                    id={`option-${option.label}`}
                    key={option.value}
                    className={classNames(styles.option, {
                      [styles.selected]: option.value === value,
                      [styles.highlighted]: index === highlightedIndex,
                    })}
                    onClick={() => {
                      setInputValue(option.value);
                      onChange(option.value);
                      setIsOpen(false);
                      setHighlightedIndex(-1);
                    }}
                    role="option"
                    // aria-selected={option.value === value}
                    aria-label={
                      option.value === value
                        ? `${option.label} נבחר`
                        : `${option.label} לא נבחר`
                    }
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : inputType === INPUT_SPECIAL_TYPE.DATE ? (
          <div className={styles.customSelect}>
            <DatePicker
              inputReadOnly={readOnly}
              value={parsedDate}
              onChange={(date) => {
                const newValue = date ? date.format(DATE_FORMAT_STORAGE) : "";
                handleInputChange({
                  target: { value: newValue },
                } as ChangeEvent<HTMLInputElement>);
              }}
              onFocus={handleDatePickerFocus}
              onBlur={handleDatePickerBlur}
              className={classNames(styles.inputDate, {
                [styles.disabled]: readOnly || disabled,
              })}
              disabled={disabled || readOnly}
              aria-describedby={errorDescribedBy}
              aria-label={label}
              format={DATE_FORMAT_DISPLAY}
              allowClear={false}
              suffixIcon={null}
              placeholder={strings.t_tax_refund_expiration_date}
              disabledDate={(current) => {
                // Disable all dates before today
                return current && current < dayjs().startOf('day');
              }}
            />
          </div>
        ) : (
          <input
            type={type}
            readOnly={readOnly}
            value={defaultInputValue || value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={classNames(styles.input, {
              [styles.disabled]: readOnly || disabled,
              [styles.numericInput]: ["numeric", "number", "tel"].includes(
                type
              ),
              [styles.emailInput]: type === INPUT_TYPE.EMAIL,
            })}
            ref={ref as React.RefObject<HTMLInputElement>}
            disabled={disabled || readOnly}
            // aria-describedby={`${name}-${label}`}
            {...(errorDescribedBy
              ? { "aria-describedby": errorDescribedBy }
              : {})}
            aria-label={label}
            placeholder={isFocused ? label : ""}
            maxLength={maxLength}
          />
        )}
        {loading && autoCompleteType != AUTOCOMPLETE.NONE && (
          <span className={styles.loader}></span>
        )}
        {!loading &&
          ((inputValue && inputValue.length > 0) || (value && value.toString().length > 0)) &&
          !readOnly &&
          inputType != INPUT_SPECIAL_TYPE.DROPDOWN &&
          inputType != INPUT_SPECIAL_TYPE.DATE && (
            <div
              className={styles.closeWrapper}
              onClick={() => {
                clear();
              }}
              tabIndex={0}
              role="button"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  clear();
                }
              }}
              // aria-label={`Clear ${label}`}
            >
              <img
                alt="clear"
                aria-label={`${strings.acc_t_clear_field} ${ariaLabel}`}
                src={images.close}
                className={styles.close}
              />
            </div>
          )}
        <label className={styles.label} id={`${name}-${label}`}>
          {isMandatory && <span className={styles.mandatoryAsterisk}>*</span>}
          {label}
        </label>
        {autoCompleteType != AUTOCOMPLETE.NONE &&
          autoCompleteType != AUTOCOMPLETE.HOUSE_NUMBER &&
          dropdownVisible &&
          renderedResults}
        {autoCompleteType == AUTOCOMPLETE.NATIONALITY &&
          renderedCountriesResults}
        {error && <div className={styles.errorMessage}>
          <ErrorMessage message={error} />
        </div>}
      </div>
    );
  }
);

export default FloatingLabelInput;
